import type { ContentSearchResult, PaginationConfig, PageSize, ContentCardItem, ContentMetadata } from '@content-search/domain';
import { useSystemConfig } from '@core/hooks/useSystemConfig';
import { Shimmer } from '@soteria/design-system-ve';
import { useCallback, useState, useEffect, useRef } from 'react';

import { ContentSearchPagination } from './ContentSearchPagination';
import { ConversationContextModal } from './ConversationContextModal';
import * as styles from './SearchResults.css';
import { createContentCardItem, ResultsList, EmptyState, ResultsControls } from './SearchResultsHelpers';

interface SearchResultsProps {
  hasSearched: boolean;
  communityId?: string;
  searchResults?: ContentSearchResult;
  pagination: PaginationConfig;
  setPagination: (pagination: PaginationConfig) => void;
  isLoading: boolean;
  onBackToResults?: () => void;
  onContextViewChange?: (showingContext: boolean) => void;
}

// Helper function to determine valid page size
const getValidPageSize = (value: string): PageSize => {
  const numValue = Number(value);

  return numValue === 25 || numValue === 50 || numValue === 100 || numValue === 200 ? numValue : 25;
};

// Main component with default values for props to avoid undefined errors
// eslint-disable-next-line complexity -- UI view switch + pagination/header rendering makes this hard to split cleanly
export function SearchResults({
  hasSearched,
  communityId,
  searchResults = {
    results: [],
    totalCount: 0,
    executionTimeMs: 0,
    pagination: { skip: 0, batchSize: 25, hasMore: false, nextSkip: 0 },
  },
  pagination = { currentPage: 1, pageSize: 25, totalPages: 1 },
  setPagination,
  isLoading = false,
  onBackToResults,
  onContextViewChange,
}: SearchResultsProps) {
  // Get system configuration including mediaServiceUrl
  const { mediaServiceUrl } = useSystemConfig();
  const [limit, setLimit] = useState(pagination.pageSize.toString());
  const [showContext, setShowContext] = useState(false);
  const [selectedContext, setSelectedContext] = useState<{
    subjectId: string;
    topicName: string;
    metadata: Record<string, unknown>;
    cardElement?: HTMLElement;
    cardRect?: DOMRect;
    contentCardItem: ContentCardItem;
  } | null>(null);

  // Track previous search results to detect when a new search is performed
  // Use a stable identifier (totalCount + first result ID) instead of object reference
  const previousSearchKeyRef = useRef<string>('');

  // Effect to automatically switch back to results when a new search is performed
  useEffect(() => {
    // Create a stable key from search results to detect actual new searches
    // This prevents Apollo cache updates from triggering false positives
    const currentSearchKey = `${searchResults.totalCount}-${searchResults.results[0]?.id || ''}`;
    const hasNewResults = currentSearchKey !== previousSearchKeyRef.current && previousSearchKeyRef.current !== '';

    if (hasNewResults && showContext) {
      // Switch back to results view when new search is performed
      setShowContext(false);
      setSelectedContext(null);
      onBackToResults?.();
    }

    // Update the ref with current search key
    previousSearchKeyRef.current = currentSearchKey;
  }, [searchResults, showContext, onBackToResults]);

  // Handle showing context view
  const handleShowContext = useCallback(
    (params: { subjectId: string; topicName: string; metadata: Record<string, unknown>; cardElement: HTMLElement }) => {
      const { subjectId, topicName, metadata, cardElement } = params;
      const cardRect = cardElement.getBoundingClientRect();

      // Find the content card item
      const selectedItem = searchResults?.results?.find(item => {
        const contentMetadata = item.contentMetadata as ContentMetadata;

        return contentMetadata?.subjectId === subjectId;
      });

      if (selectedItem) {
        const contentCardItem = createContentCardItem(selectedItem, 0);

        // Store the selected context
        setSelectedContext({ subjectId, topicName, metadata, cardElement, cardRect, contentCardItem });
        setShowContext(true);
        onContextViewChange?.(true);
      }
    },
    [searchResults?.results, onContextViewChange]
  );

  // Handle going back to results
  const handleBackToResults = useCallback(() => {
    setShowContext(false);
    setSelectedContext(null);
    onContextViewChange?.(false);
  }, [onContextViewChange]);

  // Handle limit change
  const handleLimitChange = useCallback(
    (value: string) => {
      setLimit(value);
      const newPageSize = getValidPageSize(value);

      const newPagination = {
        ...pagination,
        pageSize: newPageSize,
        currentPage: 1, // Reset to first page when changing page size
      };

      setPagination(newPagination);
    },
    [pagination, setPagination]
  );

  // Handle page change
  const handlePageChange = useCallback(
    (page: number) => {
      const newPagination = {
        ...pagination,
        currentPage: page,
      };

      setPagination(newPagination);
    },
    [pagination, setPagination]
  );

  return (
    <div>
      {/* Conversation Context Modal */}
      {selectedContext && communityId && (
        <ConversationContextModal
          communityId={communityId}
          isOpen={showContext}
          mediaServiceUrl={mediaServiceUrl}
          metadata={selectedContext.metadata}
          selectedMessage={selectedContext.contentCardItem}
          subjectId={selectedContext.subjectId}
          topicName={selectedContext.topicName}
          onClose={handleBackToResults}
        />
      )}

      {/* Results View */}
      <div>
        {/* Results Header */}
        <div className={styles.resultsHeader}>
          <div className={styles.resultsTitleRow}>
            <h2 className={styles.resultsHeading}>Results</h2>
          </div>
          <div className={styles.resultsControlsRow}>
            {searchResults?.results && searchResults.results.length > 0 && searchResults.totalCount > 0 && (
              <span className={styles.resultsItemCount}>
                {(() => {
                  const pageSize = typeof pagination.pageSize === 'number' ? pagination.pageSize : 25;
                  const startItem = (pagination.currentPage - 1) * pageSize + 1;
                  const endItem = Math.min(pagination.currentPage * pageSize, searchResults.totalCount);

                  return `${startItem}-${endItem} of ${searchResults.totalCount} items`;
                })()}
              </span>
            )}
            <ResultsControls isLoading={isLoading} limit={limit} onLimitChange={handleLimitChange} />
          </div>
        </div>

        {searchResults?.results && searchResults.results.length > 0 ? (
          <div className={styles.resultsContainer}>
            {isLoading && (
              <div className={styles.shimmerWrapper}>
                <Shimmer className={styles.shimmerLoader} />
              </div>
            )}
            <ResultsList
              mediaServiceUrl={mediaServiceUrl}
              results={searchResults.results}
              selectedSubjectId={selectedContext?.subjectId}
              onShowContext={handleShowContext}
            />

            {/* Pagination Controls */}
            {(pagination.currentPage > 1 || searchResults.pagination.hasMore) && (
              <div className={styles.paginationWrapper}>
                <ContentSearchPagination
                  currentPage={pagination.currentPage}
                  disabled={isLoading}
                  hasMore={searchResults.pagination.hasMore}
                  totalPages={
                    searchResults.totalCount > 0 && typeof pagination.pageSize === 'number'
                      ? Math.ceil(searchResults.totalCount / pagination.pageSize)
                      : undefined
                  }
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </div>
        ) : (
          <EmptyState hasSearched={hasSearched} isLoading={isLoading} />
        )}
      </div>
    </div>
  );
}
